import React, {useCallback, useMemo} from "react";
import useAccessLevels from "../../../../hooks/use-access-levels";
import {useTranslation} from "react-i18next";
import {Box, Button, Card, Theme, Typography, Unstable_Grid2 as Grid} from "@mui/material";
import {SxProps} from "@mui/system";
import {useUpdateEffect} from "react-use";
// @ts-ignore
import {ReactComponent as CanadaFlag} from '../../../../../assets/images/flags/4x3/ca.svg';
// @ts-ignore
import {ReactComponent as AmericaFlag} from '../../../../../assets/images/flags/4x3/us.svg';
// @ts-ignore
import {ReactComponent as AustraliaFlag} from '../../../../../assets/images/flags/4x3/au.svg';
// @ts-ignore
import {ReactComponent as EnglishFlag} from '../../../../../assets/images/flags/4x3/gb.svg';
// @ts-ignore
import {ReactComponent as NewZealandFlag} from '../../../../../assets/images/flags/4x3/nz.svg';
// @ts-ignore
import {ReactComponent as SpanishFlag} from '../../../../../assets/images/flags/4x3/es.svg';
// @ts-ignore
import {ReactComponent as MexicoFlag} from '../../../../../assets/images/flags/4x3/mx.svg';
// @ts-ignore
import {ReactComponent as PuertoRicoFlag} from '../../../../../assets/images/flags/4x3/pr.svg';
// @ts-ignore
import {ReactComponent as FranceFlag} from '../../../../../assets/images/flags/4x3/fr.svg';
// @ts-ignore
import {ReactComponent as SwitzerlandFlag} from '../../../../../assets/images/flags/4x3/ch.svg';
import {LayoutSettingsCachedData} from "../../../../hooks/use-custom-saved-components-state/use-panel-layout-configuration";

export interface SupportedLanguage {
    label: string;
    value: string;
    current: boolean;
}

export interface SupportedLanguagesProps {
    selectedLanguage?: LayoutSettingsCachedData['locale'];
    setCachedLayoutSettings: React.Dispatch<React.SetStateAction<LayoutSettingsCachedData['locale']>>;
}

export const SupportedLanguages: React.FC<SupportedLanguagesProps> = ({setCachedLayoutSettings, selectedLanguage}) => {
    const operatorAccessLevels = useAccessLevels();
    const {i18n: {changeLanguage, resolvedLanguage, language}, t} = useTranslation("components", {keyPrefix: 'panel-sections.drawers.settings-drawer.supported-languages'});
    const currentLanguage = useMemo(() => resolvedLanguage ?? language, [language, resolvedLanguage]);

    /**
     * Effect to change the language if the selected language is different from the current language
     */
    useUpdateEffect(() => {
        if (selectedLanguage) {
            void changeLanguage(selectedLanguage);
        }
    }, [changeLanguage, selectedLanguage])

    const supportedLanguages = useMemo<SupportedLanguage[]>(() => [
        {
            label: t(t => t['languages.english']),
            value: "en",
            current: currentLanguage === "en",
        },
        ...(operatorAccessLevels.packages.base.frenchLanguageSupport ? [{
            label: t(t => t['languages.french']),
            value: "fr",
            current: currentLanguage === "fr",
        }] : []),
        ...(operatorAccessLevels.packages.base.spanishLanguageSupport ? [{
            label: t(t => t['languages.spanish']),
            value: "es",
            current: currentLanguage === "es",
        }] : []),
    ], [currentLanguage, operatorAccessLevels.packages.base.frenchLanguageSupport, operatorAccessLevels.packages.base.spanishLanguageSupport, t])

    /**
     * Changes the selected language and saves it in the cache.
     */
    const changeSelectedLanguage = useCallback((language: SupportedLanguage) => {
        if (language.current) return;
        void changeLanguage(language.value);
        setCachedLayoutSettings(language.value)
    }, [changeLanguage, setCachedLayoutSettings])

    const outerCardSx = React.useMemo<SxProps<Theme>>(() => [
        ({
            overflow: 'visible',
            borderRadius: 2,
            paddingBlockStart: 4,
            paddingInline: 2,
            paddingBlockEnd: 2,
            position: 'relative',
            backgroundColor: 'transparent',
            borderColor: (theme) => `rgba(${theme.vars.palette.grey['500Channel']} / 12%)`,
            ".label": {
                top: -12,
                lineHeight: '22px',
                borderRadius: 2.5,
                position: 'absolute',
                display: 'inline-flex',
                alignItems: 'center',
                paddingBlock: 0,
                paddingInline: 1.25,
                color: (theme) => theme.vars.palette.common.white,
                backgroundColor: (theme) => theme.vars.palette.text.primary,
            },
        }),
        (theme) => theme.applyStyles('dark', {
            ".label": {
                color: theme.vars.palette.grey['800'],
            },
        }),
    ], [])

    const colorCardSx = React.useCallback((selected: boolean): SxProps<Theme> => ({
        minHeight: 60,
        maxWidth: 120,
        marginInline: 'auto',
        width: '100%',
        borderRadius: 2,
        padding: 2,
        borderColor: 'transparent',
        color: 'text.secondary',
        transition: (theme) => theme.transitions.create(['border-color', 'background-color', 'color'], {duration: theme.transitions.duration.short}),
        ...(selected && {
            backgroundColor: (theme) => `rgba(${theme.vars.palette.primary.mainChannel} / ${theme.vars.palette.action.selectedOpacity})`,
            color: 'primary.main',
        }),
        ...(!selected && {
            backgroundColor: 'transparent',
            "&:hover": {
                backgroundColor: (theme) => `rgba(${theme.vars.palette.grey['500Channel']} / ${theme.vars.palette.action.hoverOpacity})`,
            }
        }),
    }), []);

    /**
     * Fetching the language icon
     * @type {(function(*): (string))}
     */
    const getLanguageIcon = useCallback((language) => {
        switch (language?.toLowerCase() ?? "") {
            case "en":
            case "en-ca":
                return CanadaFlag;
            case "en-us":
                return AmericaFlag;
            case "en-au":
                return AustraliaFlag;
            case "en-gb":
            case "en-ie":
            case "en-il":
            case "en-in":
            case "en-sg":
            case "en-tt":
                return EnglishFlag;
            case "en-nz":
                return NewZealandFlag;
            case "es":
            case "es-do":
            case "es-us":
                return SpanishFlag;
            case "es-mx":
                return MexicoFlag;
            case "es-pr":
                return PuertoRicoFlag;
            case "fr":
            case "fr-ca":
                return FranceFlag;
            case "fr-ch":
                return SwitzerlandFlag;
            default:
                return null
        }
    }, [])


    return (
        <>
            <Card variant={'outlined'} sx={outerCardSx}>
                <Typography component={'span'} variant={'body2'} className={'label'} fontWeight={500}>
                    {t(t => t.title)}
                </Typography>
                <Grid container spacing={1}>
                    {
                        supportedLanguages.map((language) => (
                            <Grid xs={4} key={language.value} sx={{display: 'flex', alignItems: 'center', justifyContent: 'center',}}>
                                <Card
                                    variant={'outlined'}
                                    sx={colorCardSx(language.current)}
                                    component={Button}
                                    onClick={() => changeSelectedLanguage(language)}
                                    disableRipple
                                    disableTouchRipple
                                    disableFocusRipple
                                >
                                    <Box sx={{display: 'flex', alignItems: 'center', flexDirection: 'column', justifyContent: 'center', width: '100%', gap: 1}}>
                                        <Box component={getLanguageIcon(language.value)} color={'initial'} sx={{width: 60, height: 45, borderRadius: 1}}/>
                                        <Typography variant={'body2'} fontWeight={500} textAlign={'center'}>
                                            {language.label}
                                        </Typography>
                                    </Box>
                                </Card>
                            </Grid>
                        ))
                    }
                </Grid>
            </Card>
        </>
    );
}